import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, BuildingIcon, InfoIcon, PlusIcon, MoreHorizontalIcon, EditIcon } from 'lucide-react';
export function PayoutSettings() {
  const {
    t
  } = useTranslation('balance');
  const navigate = useNavigate();
  const merchantName = 'BilaBila Mart';
  // State for Minimum Balance section
  const [minBalanceEnabled, setMinBalanceEnabled] = useState(false);
  const [minBalanceAmount, setMinBalanceAmount] = useState('');
  const [descriptor, setDescriptor] = useState('');
  const isMinBalanceInvalid = minBalanceEnabled && (!minBalanceAmount || Number(minBalanceAmount) <= 0);
  return <div className="max-w-5xl mx-auto py-8 px-4" data-id="element-3361">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8" data-id="element-3362">
        <button onClick={() => navigate('/settings')} className="p-2 -ml-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors" aria-label={t('pages.PayoutSettings.attrs.back_to_settings')} data-id="element-3363">
          <ArrowLeftIcon size={20} data-id="element-3364" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-3365">
          {t('pages.PayoutSettings.payout_settings')}
        </h1>
      </div>

      <div className="space-y-6" data-id="element-3366">
        {/* SECTION 1: Settlement currencies and bank accounts */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-3367">
          <div className="p-6 border-b border-gray-200" data-id="element-3368">
            <h2 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-3369">
              {t('pages.PayoutSettings.settlement_currencies_and_bank_accounts')}
            </h2>
            <p className="text-sm text-gray-500" data-id="element-3370">
              {t('pages.PayoutSettings.to_accumulate_a_balance_in_a')}
            </p>
          </div>

          <div className="overflow-x-auto" data-id="element-3371">
            <table className="w-full text-left border-collapse" data-id="element-3372">
              <thead data-id="element-3373">
                <tr className="bg-gray-50/50 border-b border-gray-200" data-id="element-3374">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-32" data-id="element-3375">
                    {t('pages.PayoutSettings.currency')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3376">
                    {t('pages.PayoutSettings.payout_bank_account')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-48" data-id="element-3377">
                    <div className="flex items-center gap-1.5" data-id="element-3378">
                      {t('pages.PayoutSettings.payout_fee')}
                      <InfoIcon size={14} className="text-gray-400" data-id="element-3379" />
                    </div>
                  </th>
                  <th className="px-6 py-3 w-24" data-id="element-3380"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-3381">
                <tr className="hover:bg-gray-50/50 transition-colors" data-id="element-3382">
                  <td className="px-6 py-4" data-id="element-3383">
                    <div className="flex items-center gap-2" data-id="element-3384">
                      <span className="text-sm font-medium text-gray-900" data-id="element-3385">
                        {t('pages.BalanceActivity.usd')}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-100 text-emerald-800" data-id="element-3386">
                        {t('pages.PayoutSettings.default')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-3387">
                    <div className="flex items-center gap-3" data-id="element-3388">
                      <div className="w-8 h-8 rounded bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-3389">
                        <BuildingIcon size={16} className="text-gray-500" data-id="element-3390" />
                      </div>
                      <div className="flex items-center gap-2" data-id="element-3391">
                        <span className="text-sm text-gray-900" data-id="element-3392">
                          {t('pages.PayoutSettings.kasikorn_bank')}
                        </span>
                        <span className="text-sm text-gray-500 font-mono" data-id="element-3393">
                          ••••0536 / 7375-001
                        </span>
                        <span className="text-lg leading-none" title={t('pages.PayoutSettings.attrs.thailand')} data-id="element-3394">
                          🇹🇭
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-3395">
                    <span className="text-sm text-gray-600" data-id="element-3396">
                      {t('pages.PayoutSettings.free')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-3397">
                    <div className="flex items-center justify-end gap-2" data-id="element-3398">
                      <button className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors shadow-sm" data-id="element-3399">
                        {t('pages.PayoutSettings.edit')}
                      </button>
                      <button aria-label="Row actions" className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors border border-transparent" data-id="element-3400">
                        <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-3401" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50/30" data-id="element-3402">
            <button className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors" data-id="element-3403">
              {t('pages.PayoutSettings.add_a_settlement_currency')}
            </button>
          </div>
        </div>

        {/* SECTION 2: Minimum balance */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-3404">
          <div className="p-6 border-b border-gray-200" data-id="element-3405">
            <h2 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-3406">
              {t('pages.PayoutSettings.minimum_balance')}
            </h2>
            <p className="text-sm text-gray-500" data-id="element-3407">
              {t('pages.PayoutSettings.keep_a_minimum_amount_in_your')}
            </p>
          </div>

          <div className="p-6" data-id="element-3408">
            <div className="flex items-start gap-4" data-id="element-3409">
              <button type="button" role="switch" aria-checked={minBalanceEnabled} onClick={() => setMinBalanceEnabled(!minBalanceEnabled)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 mt-0.5 ${minBalanceEnabled ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-3410">
                <span aria-hidden="true" className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${minBalanceEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-3411" />
              </button>

              <div className="flex-1" data-id="element-3412">
                <label className="text-sm font-medium text-gray-900 cursor-pointer block" onClick={() => setMinBalanceEnabled(!minBalanceEnabled)} data-id="element-3413">
                  {t('pages.PayoutSettings.keep_a_minimum_amount_in_your_2')}
                </label>
                <p className="text-sm text-gray-500 mt-0.5 mb-4" data-id="element-3414">
                  {t('pages.PayoutSettings.we_will_adjust_your_payouts_so')}
                </p>

                {minBalanceEnabled && <div className="mb-4" data-id="element-3415">
                    <div className="relative max-w-xs" data-id="element-3416">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3417">
                        <span className="text-gray-500 sm:text-sm" data-id="element-3418">
                          {t('pages.BalanceActivity.usd')}
                        </span>
                      </div>
                      <input type="number" value={minBalanceAmount} onChange={e => setMinBalanceAmount(e.target.value)} className={`block w-full pl-12 pr-3 py-2 sm:text-sm rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${isMinBalanceInvalid ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`} placeholder="0" data-id="element-3419" />
                    </div>
                    {isMinBalanceInvalid && <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5" data-id="element-3420">
                        <InfoIcon size={14} data-id="element-3421" />
                        {t('pages.PayoutSettings.the_amount_must_be_greater_than')}
                      </p>}
                  </div>}

                <button className="flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors" data-id="element-3422">
                  <PlusIcon size={16} data-id="element-3423" />
                  {t('pages.PayoutSettings.add_another_currency')}
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-3424">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors shadow-sm" data-id="element-3425">
              {t('pages.BalanceSummary.cancel')}
            </button>
            <button disabled={isMinBalanceInvalid} className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors shadow-sm ${isMinBalanceInvalid ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`} data-id="element-3426">
              {t('pages.PayoutSettings.save')}
            </button>
          </div>
        </div>

        {/* SECTION 3: Payout statement descriptor */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-3427">
          <div className="p-6 border-b border-gray-200" data-id="element-3428">
            <h2 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-3429">
              {t('pages.PayoutSettings.payout_statement_descriptor')}
            </h2>
            <p className="text-sm text-gray-500" data-id="element-3430">
              {t('pages.PayoutSettings.select_a_statement_descriptor_for_payouts')}
            </p>
          </div>

          <div className="p-6 space-y-6" data-id="element-3431">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3" data-id="element-3432">
              <InfoIcon size={20} className="text-gray-500 flex-shrink-0 mt-0.5" data-id="element-3433" />

              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-3434">
                {t('pages.PayoutSettings.you_can_set_a_custom_statement')}{' '}
                <button className="text-indigo-600 hover:text-indigo-800 hover:underline" data-id="element-3435">
                  {t('pages.PayoutSettings.balances')}
                </button>
                {t('pages.PayoutSettings.overriding_this_accountlevel_setting')}
              </p>
            </div>

            <div data-id="element-3436">
              <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-3437">
                {t('pages.PayoutSettings.statement_descriptor')}
              </label>
              <input type="text" value={descriptor} onChange={e => setDescriptor(e.target.value)} placeholder={`Example - ${merchantName}`} className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-3438" />
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-3439">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors shadow-sm" data-id="element-3440">
              {t('pages.BalanceSummary.cancel')}
            </button>
            <button className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors shadow-sm" data-id="element-3441">
              {t('pages.PayoutSettings.save')}
            </button>
          </div>
        </div>
      </div>
    </div>;
}